/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faCalendarAlt } from '@fortawesome/pro-duotone-svg-icons/faCalendarAlt';


import { DatePicker as MuiDatePicker, DatePickerProps as MuiDatePickerProps } from '@material-ui/pickers';

import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

import { FieldRenderProps } from 'react-final-form';

import { DateTime } from 'luxon';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import InputAdornment from '@material-ui/core/InputAdornment';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */
type DatePickerProps = Partial<Omit<MuiDatePickerProps, 'type'>> & FieldRenderProps<any, any>

interface IDatePicker extends DatePickerProps {
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const DatePicker: React.FunctionComponent<IDatePicker> = ({
    input: { name, value, onChange: onInputChange, checked, onBlur, onFocus },
    meta,
    fullWidth,
    onChange,
    clearable,
    format,
    InputProps,
    ...rest
}) => {

    const onChangeCombined = React.useCallback((date: MaterialUiPickersDate) => {
        if (onChange) {
            onChange(date);
        }

        onInputChange(date?.toISO({ includeOffset: false }));
    }, [onChange, onInputChange]);

    const derivedValue = React.useMemo(() => {
        if (!value) {
            return null;
        }

        return DateTime.fromISO(value);
    }, [value])

    const DerivedInputProps = React.useMemo(() => {
        if (!InputProps) {
            return {
                endAdornment: (
                    <InputAdornment
                        position="end"
                    >
                        <FontAwesomeIcon icon={faCalendarAlt} fixedWidth />
                    </InputAdornment>
                )
            };
        }

        if (InputProps.endAdornment !== undefined) {
            return InputProps;
        }

        return {
            ...InputProps,
            endAdornment: (
                <InputAdornment
                    position="end"
                >
                    <FontAwesomeIcon icon={faCalendarAlt} fixedWidth />
                </InputAdornment>
            )
        }
    }, [InputProps]);


    const derivedInputProps = React.useMemo(() => {
        return {
            onBlur,
            onFocus
        };
    }, [onBlur, onFocus]);

    return (
        <MuiDatePicker
            {...rest as any}
            fullWidth={fullWidth === undefined ? true : fullWidth}
            format={format === undefined ? 'dd/MM/yyyy' : format }
            clearable={clearable === undefined ? true : clearable}
            InputProps={DerivedInputProps}
            inputProps={derivedInputProps}
            name={name}
            value={derivedValue}
            onChange={onChangeCombined}
        />
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default DatePicker;
